import { DOCUMENT } from '@angular/common';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DestroyRef,
  Injectable,
  OutputRef,
  Type,
  inject,
  signal,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { EInitEventTypeCode } from '@core/enums/init-event-type.enum';
import { AppCapabilityService } from '@core/services/app-capability.service';
import { AuthSessionBoundaryService } from '@core/services/auth-session-boundary.service';
import { Store } from '@ngrx/store';
import { APP_TRANSIENT_HISTORY_MARKER_KEY } from '@core/models/app-history.model';
import {
  Observable,
  Subscription,
  filter,
  firstValueFrom,
  isObservable,
  of,
  take,
} from 'rxjs';

import { EventListActions } from '../event-list/state/event-list.actions';
import {
  EventCreationDialogData,
  EventCreationDialogRequest,
  EventCreationDialogResult,
} from './event-creation-dialog.model';

const OVERLAY_OPEN_CLASS = 'event-creation-dialog-open';

interface EventCreationDialogComponentContract {
  readonly closeRequested: OutputRef<void>;
  readonly created: OutputRef<void>;
  canClose(): boolean | Observable<boolean>;
  cancelPendingClose(): void;
}

interface ActiveEventCreationDialog {
  readonly ref: DialogRef<
    EventCreationDialogResult,
    EventCreationDialogComponentContract
  >;
  readonly component: EventCreationDialogComponentContract;
  readonly markerId: string;
  readonly subscriptions: Subscription;
  closeAttempt: Promise<void> | null;
  pendingResult: EventCreationDialogResult | null;
  bypassNextPopstate: boolean;
  closing: boolean;
  preserveMarkerOnClose: boolean;
}

@Injectable({ providedIn: 'root' })
export class EventCreationDialogService {
  private readonly capabilities = inject(AppCapabilityService);
  private readonly sessionBoundary = inject(AuthSessionBoundaryService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(Dialog);
  private readonly document = inject(DOCUMENT);
  private readonly overlay = inject(Overlay);
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly openedState = signal(false);

  public readonly opened = this.openedState.asReadonly();

  private activeDialog: ActiveEventCreationDialog | null = null;
  private pendingOpen: Promise<EventCreationDialogResult> | null = null;
  private markerSequence = 0;
  private navigationEpoch = 0;
  private sessionEpoch = 0;
  private staleMarkerRollbackPending = false;

  constructor() {
    this.window?.addEventListener('popstate', this.handlePopstate);
    this.destroyRef.onDestroy(() => {
      this.window?.removeEventListener('popstate', this.handlePopstate);
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.navigationEpoch += 1;
      });
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const activeDialog = this.activeDialog;
        if (activeDialog && this.currentMarkerId() !== activeDialog.markerId) {
          this.pushMarker(activeDialog);
        }
      });

    this.sessionBoundary.synchronizationStarted
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.sessionEpoch += 1;
      });
    this.sessionBoundary.synchronizationReleased
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const activeDialog = this.activeDialog;
        if (activeDialog && !this.capabilities.canCreateEvent()) {
          this.forceCloseActiveDialog();
        }
      });
    this.sessionBoundary.crossed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.invalidateSession());

    this.rollbackStaleMarker();
  }

  public open(
    request: EventCreationDialogRequest,
  ): Promise<EventCreationDialogResult> {
    if (this.pendingOpen) {
      return this.pendingOpen;
    }

    const pendingOpen = this.openDialog(request);
    this.pendingOpen = pendingOpen;
    void pendingOpen.then(
      () => this.clearPendingOpen(pendingOpen),
      () => this.clearPendingOpen(pendingOpen),
    );

    return pendingOpen;
  }

  private async openDialog(
    request: EventCreationDialogRequest,
  ): Promise<EventCreationDialogResult> {
    if (!this.canCreate(request.initialEventType)) {
      return 'denied';
    }

    const openingNavigationEpoch = this.navigationEpoch;
    const openingSessionEpoch = this.sessionEpoch;
    const openingUrl = this.router.url;
    const { AddSecurityEventComponent } =
      await import('./add-security-event.component');

    if (
      this.navigationEpoch !== openingNavigationEpoch ||
      this.sessionEpoch !== openingSessionEpoch ||
      this.router.url !== openingUrl
    ) {
      return 'cancelled';
    }

    if (!this.canCreate(request.initialEventType)) {
      return 'denied';
    }

    const data: EventCreationDialogData = {
      initialEventType: request.initialEventType,
      ...(request.prefill ? { prefill: request.prefill } : {}),
      ...(request.sourceMessage
        ? { sourceMessage: request.sourceMessage }
        : {}),
    };
    const ref = this.dialog.open<
      EventCreationDialogResult,
      EventCreationDialogData,
      EventCreationDialogComponentContract
    >(AddSecurityEventComponent as Type<EventCreationDialogComponentContract>, {
      data,
      hasBackdrop: true,
      panelClass: 'event-creation-dialog-pane',
      backdropClass: 'event-creation-dialog-backdrop',
      width: '100vw',
      height: 'var(--app-visible-viewport-height, 100dvh)',
      maxWidth: '100vw',
      maxHeight: 'var(--app-visible-viewport-height, 100dvh)',
      positionStrategy: this.overlay.position().global().top('0').left('0'),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      disableClose: true,
      closeOnNavigation: false,
      role: 'dialog',
      ariaLabelledBy: 'app-overlay-shell-title',
      ariaModal: false,
      autoFocus: 'dialog',
      restoreFocus: request.restoreFocus ?? true,
    });
    const component = ref.componentInstance;
    if (!component) {
      ref.close('cancelled');
      return 'cancelled';
    }

    const activeDialog: ActiveEventCreationDialog = {
      ref,
      component,
      markerId: this.createMarkerId(),
      subscriptions: new Subscription(),
      closeAttempt: null,
      pendingResult: null,
      bypassNextPopstate: false,
      closing: false,
      preserveMarkerOnClose: false,
    };
    this.activeDialog = activeDialog;
    this.openedState.set(true);
    this.overlayContainer
      .getContainerElement()
      .classList.add(OVERLAY_OPEN_CLASS);

    try {
      this.pushMarker(activeDialog);
      this.bindCloseSources(activeDialog);
      const result = await firstValueFrom(ref.closed.pipe(take(1)), {
        defaultValue: 'cancelled' as const,
      });
      const normalizedResult = result ?? 'cancelled';
      if (normalizedResult === 'created') {
        this.store.dispatch(EventListActions.requestEvents());
      }
      return normalizedResult;
    } finally {
      activeDialog.subscriptions.unsubscribe();
      if (this.activeDialog === activeDialog) {
        if (!activeDialog.preserveMarkerOnClose) {
          this.stripCurrentMarker(activeDialog.markerId);
        }
        this.activeDialog = null;
        this.openedState.set(false);
      }
      this.overlayContainer
        .getContainerElement()
        .classList.remove(OVERLAY_OPEN_CLASS);
    }
  }

  private bindCloseSources(activeDialog: ActiveEventCreationDialog): void {
    activeDialog.subscriptions.add(
      activeDialog.component.closeRequested.subscribe(() => {
        this.requestClose(activeDialog, 'cancelled');
      }),
    );
    activeDialog.subscriptions.add(
      activeDialog.component.created.subscribe(() => {
        this.requestClose(activeDialog, 'created');
      }),
    );
    activeDialog.subscriptions.add(
      activeDialog.ref.backdropClick.subscribe(() => {
        this.requestClose(activeDialog, 'cancelled');
      }),
    );
    activeDialog.subscriptions.add(
      activeDialog.ref.keydownEvents.subscribe((event) => {
        if (event.key !== 'Escape') {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.requestClose(activeDialog, 'cancelled');
      }),
    );
  }

  private requestClose(
    activeDialog: ActiveEventCreationDialog,
    result: EventCreationDialogResult,
  ): void {
    if (
      this.activeDialog !== activeDialog ||
      activeDialog.closing ||
      activeDialog.closeAttempt
    ) {
      return;
    }

    const closeAttempt = this.tryClose(activeDialog, result);
    activeDialog.closeAttempt = closeAttempt;
    const clearCloseAttempt = (): void => {
      if (activeDialog.closeAttempt === closeAttempt) {
        activeDialog.closeAttempt = null;
      }
    };
    void closeAttempt.then(clearCloseAttempt, clearCloseAttempt);
  }

  private async tryClose(
    activeDialog: ActiveEventCreationDialog,
    result: EventCreationDialogResult,
  ): Promise<void> {
    const closeDecision = activeDialog.component.canClose();
    const canClose = await firstValueFrom(
      (isObservable(closeDecision) ? closeDecision : of(closeDecision)).pipe(
        take(1),
      ),
      { defaultValue: false },
    );

    if (
      !canClose ||
      this.activeDialog !== activeDialog ||
      activeDialog.closing
    ) {
      return;
    }

    activeDialog.closing = true;
    this.popMarkerThenClose(activeDialog, result);
  }

  private popMarkerThenClose(
    activeDialog: ActiveEventCreationDialog,
    result: EventCreationDialogResult,
  ): void {
    if (this.currentMarkerId() !== activeDialog.markerId || !this.history) {
      activeDialog.ref.close(result);
      return;
    }

    activeDialog.pendingResult = result;
    activeDialog.bypassNextPopstate = true;
    this.history.back();
  }

  private readonly handlePopstate = (event: PopStateEvent): void => {
    const activeDialog = this.activeDialog;
    const eventMarkerId = this.markerId(event.state);

    if (!activeDialog) {
      if (this.staleMarkerRollbackPending) {
        this.staleMarkerRollbackPending = false;
        if (eventMarkerId) {
          this.rollbackStaleMarker();
        }
        return;
      }

      if (eventMarkerId) {
        this.rollbackStaleMarker();
      }
      return;
    }

    if (activeDialog.bypassNextPopstate) {
      activeDialog.bypassNextPopstate = false;
      const result = activeDialog.pendingResult ?? 'cancelled';
      activeDialog.pendingResult = null;
      activeDialog.ref.close(result);
      return;
    }

    if (eventMarkerId === activeDialog.markerId) {
      return;
    }

    this.pushMarker(activeDialog, event.state);
    this.requestClose(activeDialog, 'cancelled');
  };

  private canCreate(eventType: EInitEventTypeCode | null): boolean {
    switch (eventType) {
      case null:
        return this.capabilities.canCreateEvent();
      case EInitEventTypeCode.SB:
        return this.capabilities.canCreateSb();
      case EInitEventTypeCode.NNR:
        return this.capabilities.canCreateNnr();
      default:
        return false;
    }
  }

  private invalidateSession(): void {
    this.sessionEpoch += 1;
    this.forceCloseActiveDialog(true);
  }

  private forceCloseActiveDialog(preserveMarkerOnClose = false): void {
    const activeDialog = this.activeDialog;
    if (!activeDialog || activeDialog.closing) {
      return;
    }

    activeDialog.closing = true;
    activeDialog.pendingResult = null;
    activeDialog.bypassNextPopstate = false;
    activeDialog.preserveMarkerOnClose = preserveMarkerOnClose;
    activeDialog.component.cancelPendingClose();
    if (preserveMarkerOnClose) {
      activeDialog.ref.close('cancelled');
    } else {
      this.popMarkerThenClose(activeDialog, 'cancelled');
    }
  }

  private pushMarker(
    activeDialog: ActiveEventCreationDialog,
    state: unknown = this.history?.state,
  ): void {
    this.history?.pushState(
      {
        ...this.asHistoryState(state),
        [APP_TRANSIENT_HISTORY_MARKER_KEY]: activeDialog.markerId,
      },
      '',
    );
  }

  private rollbackStaleMarker(): void {
    if (
      this.activeDialog ||
      this.staleMarkerRollbackPending ||
      !this.currentMarkerId() ||
      !this.history
    ) {
      return;
    }

    this.staleMarkerRollbackPending = true;
    this.history.back();
  }

  private stripCurrentMarker(expectedMarkerId: string): void {
    if (this.currentMarkerId() === expectedMarkerId) {
      this.replaceHistoryState(this.withoutMarker(this.history?.state));
    }
  }

  private replaceHistoryState(state: Record<string, unknown>): void {
    this.history?.replaceState(state, '');
  }

  private withoutMarker(state: unknown): Record<string, unknown> {
    const clone = this.asHistoryState(state);
    delete clone[APP_TRANSIENT_HISTORY_MARKER_KEY];
    return clone;
  }

  private asHistoryState(state: unknown): Record<string, unknown> {
    return state !== null && typeof state === 'object' && !Array.isArray(state)
      ? { ...(state as Record<string, unknown>) }
      : {};
  }

  private currentMarkerId(): string | null {
    return this.markerId(this.history?.state);
  }

  private markerId(state: unknown): string | null {
    const value = this.asHistoryState(state)[APP_TRANSIENT_HISTORY_MARKER_KEY];
    return typeof value === 'string' ? value : null;
  }

  private createMarkerId(): string {
    this.markerSequence += 1;
    return `${Date.now()}-${this.markerSequence}`;
  }

  private clearPendingOpen(
    pendingOpen: Promise<EventCreationDialogResult>,
  ): void {
    if (this.pendingOpen === pendingOpen) {
      this.pendingOpen = null;
    }
  }

  private get window(): Window | null {
    return this.document.defaultView;
  }

  private get history(): History | null {
    return this.window?.history ?? null;
  }
}
